import { useEffect, useRef, useState } from 'react'
import { useQuery } from 'react-query'
import { toast } from 'react-toastify'
import { FiBell } from 'react-icons/fi'

import { getWebPushConfig } from 'services/webPush'
import {
    EnableStage,
    enableWebPush,
    isPromptAllowed,
    isWebPushOn,
    notificationPermission,
    pushAvailability,
    readPromptState,
    rememberPromptDismissed,
} from 'modules/webPush'
import { queueEvent } from 'utilities/analytics'

/**
 * «یادآوری‌ها را از دست ندهید»: our own ask, shown before the browser's.
 *
 * The browser's permission dialog is a one-shot: «Block» there is permanent
 * for most people. So it only ever opens from the button on this card, and the
 * card itself is shy:
 *  - only where a push can actually arrive (not in a mini app, not in an
 *    iPhone Safari tab, not on a browser without push),
 *  - never once the browser has blocked us or this browser is already on,
 *  - only for someone who has a fund (there is nothing to remind anyone else
 *    of), which the home page decides by where it renders this,
 *  - «الان نه» is two weeks of silence, and the third one is for good.
 * The settings page under «اعلان‌ها» stays available whatever happens here.
 */
export default function WebPushOptInCard() {
    const [eligible] = useState(
        () =>
            pushAvailability() === 'ok' &&
            notificationPermission() !== 'denied' &&
            !isWebPushOn() &&
            isPromptAllowed(readPromptState(), Date.now())
    )
    const [hidden, setHidden] = useState(false)
    const [busy, setBusy] = useState(false)
    const [stage, setStage] = useState<EnableStage | null>(null)
    const shownTracked = useRef(false)

    const { data: config } = useQuery(['web-push-config'], getWebPushConfig, {
        enabled: eligible,
        staleTime: 5 * 60_000,
        retry: false,
    })
    const visible = eligible && !hidden && !!config?.enabled

    useEffect(() => {
        if (!visible || shownTracked.current) return
        shownTracked.current = true
        queueEvent('web_push_prompt_shown', {
            permission: notificationPermission(),
            dismissals: readPromptState().dismissals,
        })
    }, [visible])

    if (!visible) return null

    const accept = async () => {
        if (busy) return
        setBusy(true)
        queueEvent('web_push_prompt_accepted', {
            permission: notificationPermission(),
        })
        const result = await enableWebPush('home_card', setStage).finally(() => {
            setBusy(false)
            setStage(null)
        })
        switch (result) {
            case 'subscribed':
                toast.success('اعلان‌ها روشن شد. کارهای مهم را همین‌جا خبر می‌دهیم.')
                setHidden(true)
                break
            case 'denied':
                toast.info(
                    'اعلان‌ها در مرورگر مسدود شد. هر وقت خواستید از منوی «اعلان‌ها» روشنش کنید.'
                )
                setHidden(true)
                break
            case 'dismissed':
                // Closed the browser's dialog without answering: that is a
                // «not now», and Chrome itself goes quiet after a few of them.
                rememberPromptDismissed()
                setHidden(true)
                break
            case 'unavailable':
                setHidden(true)
                break
            default:
                toast.error('روشن کردن اعلان‌ها انجام نشد. کمی بعد دوباره امتحان کنید.')
        }
    }

    const dismiss = () => {
        const next = rememberPromptDismissed()
        queueEvent('web_push_prompt_dismissed', { dismissals: next.dismissals })
        setHidden(true)
    }

    return (
        <div className="bg-white rounded-lg shadow-header p-4 flex flex-col gap-3">
            <div className="flex gap-3">
                <span className="shrink-0 w-10 h-10 rounded-full bg-brand-soft text-brand-deep flex items-center justify-center">
                    <FiBell size={20} />
                </span>
                <div className="min-w-0">
                    <p className="text-sm font-bold">
                        یادآوری‌ها را از دست ندهید
                    </p>
                    <p className="text-xs text-text-secondary mt-1 leading-6">
                        سررسید قسط و حق عضویت، درخواستی که منتظر تأیید شماست و
                        رسید پرداخت‌ها را همین‌جا خبر می‌دهیم. شب‌ها مزاحم
                        نمی‌شویم.
                    </p>
                </div>
            </div>
            <div className="flex gap-2">
                <button
                    type="button"
                    onClick={accept}
                    disabled={busy}
                    className="bg-green-1 text-white text-sm rounded-md py-2 px-4 grow disabled:opacity-60"
                >
                    {!busy
                        ? 'فعال‌سازی اعلان‌ها'
                        : stage === 'permission'
                          ? 'منتظر اجازهٔ مرورگر…'
                          : 'در حال روشن کردن…'}
                </button>
                <button
                    type="button"
                    onClick={dismiss}
                    disabled={busy}
                    className="text-text-secondary text-sm rounded-md py-2 px-4 border border-divider"
                >
                    الان نه
                </button>
            </div>
        </div>
    )
}
